/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Single row in the chapter content tree.
 *
 * @file ChapterContentTreeRow.jsx
 * @author Gladson Samuel S
 * @since 2.0.0
 */

import React from 'react';
import PropTypes from 'prop-types';
import {
  Checkbox,
  IconButton,
  Typography
} from '@mui/material';
import Icon from '../icons/Icon';
import ThreeDotActionMenu from '../common/ThreeDotActionMenu';
import CheckboxOutlineIcon from '../icons/CheckboxOutlineIcon';
import CheckboxCheckedIcon from '../icons/CheckboxCheckedIcon';
import TooltipWrapper from '../tooltip/TooltipWrapper';
import { useChapterContent } from './ChapterContentContext';
import { useChapterTreeExpansionContext } from './useChapterTreeExpansion';
import {
  buildDragPayload,
  buildScheduleMenuItems,
  calculateQuestionsForNode,
  calculateQuestionsForSelectedNode,
  getChapterRowDisplayState,
  getQuestionCountText,
  getScheduleMenuHiddenItemIds,
  joinClasses
} from './chapterContentTreeUtils';
import { classes } from './styles/chapterContentTreeStyles';

const ChapterContentTreeRowBase = ({ node, level }) => {
  const {
    nodes,
    questionCounts,
    textInfo,
    restrictAssigned,
    variant,
    onScheduleMenuAction,
    onAddToDate
  } = useChapterContent();

  const {
    expandedNodes,
    selectedNodes,
    handleToggleExpanded,
    handleToggleNode
  } = useChapterTreeExpansionContext();

  const rowState = getChapterRowDisplayState({
    node,
    level,
    selectedNodes,
    restrictAssigned,
    variant,
    expandedNodes,
    textInfo,
    questionCounts,
    nodes,
    classes
  });

  const selectedQuestionCount = calculateQuestionsForSelectedNode(
    node,
    selectedNodes,
    questionCounts,
    nodes
  );

  const nodeQuestionCount = rowState.isScheduleVariant
    ? calculateQuestionsForNode(node, questionCounts, nodes)
    : selectedQuestionCount;

  const scheduleMenuItems = buildScheduleMenuItems(
    node,
    onAddToDate,
    onScheduleMenuAction,
    {
      textInfo,
      hiddenMenuItemIds: getScheduleMenuHiddenItemIds(textInfo, {
        isChecked: rowState.isChecked,
        isIndeterminate: rowState.isIndeterminate,
        selectedQuestionCount,
        nodeQuestionCount,
        isAllDescendantsAssigned: rowState.isAllDescendantsAssigned
      })
    }
  );

  const handleCheckboxChange = () => {
    handleToggleNode(node);
  };

  const handleDragStart = (event) => {
    event.dataTransfer.setData('application/json', buildDragPayload(node));
    // eslint-disable-next-line no-param-reassign
    event.dataTransfer.effectAllowed = 'copy';
  };

  const renderExpandButton = () => {
    if (!rowState.hasChildren) {
      return null;
    }

    return (
      <IconButton
        onClick={(event) => {
          event.stopPropagation();
          handleToggleExpanded(rowState.nodeId);
        }}
        className={classes.expandButton}
        size="small"
        aria-label={rowState.isExpanded ? 'Collapse section' : 'Expand section'}
        aria-expanded={rowState.isExpanded}
      >
        {rowState.isExpanded ? <Icon name="CaretDownIcon" /> : <Icon name="CaretUpIcon" />}
      </IconButton>
    );
  };

  const renderDragHandle = () => {
    if (!rowState.showDragHandle) {
      return null;
    }

    const handleClassName = joinClasses(
      classes.dragHandle,
      !rowState.isDraggable && classes.dragHandleDisabled
    );

    if (rowState.isDraggable) {
      return (
        <span className={handleClassName} aria-hidden="true">
          <Icon name="DragHandleIcon" />
        </span>
      );
    }

    const disabledHandle = (
      <span
        className={handleClassName}
        aria-hidden="true"
        onDragStart={(event) => event.preventDefault()}
      >
        <Icon name="DragHandleIcon" />
      </span>
    );

    return (
      <TooltipWrapper
        showTooltip={Boolean(rowState.tooltipText)}
        tooltip={rowState.tooltipText || ''}
        placement="top-start"
        popperProps={{
          modifiers: {
            offset: {
              offset: '24px, 0',
              enabled: true
            }
          }
        }}
      >
        <span className={classes.dragHandleWrapper}>
          {disabledHandle}
        </span>
      </TooltipWrapper>
    );
  };

  const renderCheckboxLabel = () => (
    <TooltipWrapper
      showTooltip={rowState.isPickerVariant && Boolean(rowState.tooltipText)}
      tooltip={rowState.tooltipText || ''}
      placement="top-start"
      popperProps={{
        modifiers: {
          offset: {
            offset: '24px, 0',
            enabled: true
          }
        }
      }}
    >
      <div className={classes.checkboxWrapper}>
        {renderDragHandle()}
        {rowState.isPickerVariant && (
          <Checkbox
            checked={rowState.isChecked && !rowState.isDisabled}
            indeterminate={rowState.isIndeterminate}
            disabled={rowState.isDisabled}
            onChange={handleCheckboxChange}
            onClick={(event) => event.stopPropagation()}
            color="primary"
            disableRipple
            className={classes.checkboxRoot}
            icon={<CheckboxOutlineIcon className={classes.checkboxIcon} />}
            checkedIcon={<CheckboxCheckedIcon />}
            inputProps={{
              'aria-labelledby': `chapter-content-select-${rowState.nodeId}`
            }}
          />
        )}
        <Typography
          id={`chapter-content-select-${rowState.nodeId}`}
          component="span"
          className={rowState.labelClassName}
        >
          {node.title}
        </Typography>
      </div>
    </TooltipWrapper>
  );

  const renderMeta = () => {
    if (rowState.showAssignedStatus) {
      return (
        <Typography component="span" className={classes.statusLabel}>
          {rowState.statusLabel}
        </Typography>
      );
    }

    if (!rowState.showQuestionCount) {
      return null;
    }

    return (
      <Typography component="span" className={classes.questionCount}>
        {getQuestionCountText(node, questionCounts, nodes, textInfo)}
      </Typography>
    );
  };

  const renderActionMenu = () => {
    if (!rowState.isTopLevel) {
      return null;
    }

    if (!rowState.isScheduleVariant && !rowState.isPickerVariant) {
      return null;
    }

    return (
      <ThreeDotActionMenu
        menuItems={scheduleMenuItems}
        ariaLabel="Chapter actions"
      />
    );
  };

  const renderRowContent = () => (
    <div className={rowState.rowContentClassName} style={rowState.rowDepthStyle}>
      {renderExpandButton()}
      {renderCheckboxLabel()}
      {renderMeta()}
    </div>
  );

  const renderScheduleRow = () => (
    <div
      className={classes.rowOuter}
      draggable={rowState.isDraggable}
      onDragStart={rowState.isDraggable ? handleDragStart : undefined}
    >
      <div className={joinClasses(
        classes.rowContentMain,
        rowState.isDraggable && classes.rowContentDraggable
      )}
      >
        {renderRowContent()}
      </div>
      {renderActionMenu()}
    </div>
  );

  const renderTopLevelRow = () => (
    <div
      className={classes.rowOuter}
      draggable={rowState.isDraggable}
      onDragStart={rowState.isDraggable ? handleDragStart : undefined}
    >
      <div className={joinClasses(
        classes.rowContentMain,
        rowState.isDraggable && classes.rowContentDraggable
      )}
      >
        {renderRowContent()}
      </div>
      {renderActionMenu()}
    </div>
  );

  const renderChildRows = () => {
    if (!rowState.hasChildren || !rowState.isExpanded) {
      return null;
    }

    return (
      <div className={classes.children}>
        {rowState.children.map((child) => (
          <ChapterContentTreeRowBase
            key={child.id}
            classes={classes}
            node={child}
            level={level + 1}
          />
        ))}
      </div>
    );
  };

  const renderRow = () => {
    if (rowState.isScheduleVariant) {
      return renderScheduleRow();
    }

    if (rowState.isTopLevel) {
      return renderTopLevelRow();
    }

    return renderRowContent();
  };

  return (
    <div className={rowState.wrapperClassName}>
      <div className={classes.row}>
        {renderRow()}
        {renderChildRows()}
      </div>
    </div>
  );
};

ChapterContentTreeRowBase.propTypes = {
  node: PropTypes.object.isRequired,
  level: PropTypes.number.isRequired
};

export default ChapterContentTreeRowBase;
